import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Heart, Clock, Users, Link2, Paperclip, ListChecks, History } from "lucide-react";
import { useLibraryAssets, useLibraryLinks, useLibraryRecentUsage, useToggleLibraryFavorite } from "@/lib/coachWorkbenchQueries";

const ROLE_LABEL = {
  worksheet: "Worksheet", instructions: "Instructions", coach_guide: "Coach guide", demo_video: "Demo video",
  demo_audio: "Demo audio", slides: "Slides", transcript: "Transcript", reference: "Reference",
  thumbnail: "Thumbnail", preview: "Preview",
};

/**
 * Master v5 Prompt 20 checkpoint 2 — the full card Quick Preview.
 * Rendered as a Radix Dialog Portal so the underlying catalog list is
 * NEVER unmounted — opening/closing this preserves the list's exact
 * scroll position and search/filter results untouched.
 */
export function LibraryQuickPreviewModal({ item, latest, isFavorite, open, onOpenChange }) {
  const { data: assetsData } = useLibraryAssets(open ? item?.lib_id : null);
  const { data: linksData } = useLibraryLinks(open ? item?.lib_id : null);
  const { data: usageData } = useLibraryRecentUsage(open ? item?.lib_id : null);
  const toggleFavorite = useToggleLibraryFavorite();

  if (!item || !latest) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`library-quick-preview-${item.lib_id}`}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="font-serif">{item.title}</DialogTitle>
            <button onClick={() => toggleFavorite.mutate(item.lib_id)} className={isFavorite ? "text-terracotta" : "text-ink-soft hover:text-terracotta"} data-testid={`library-quick-preview-favorite-${item.lib_id}`}>
              <Heart size={15} fill={isFavorite ? "currentColor" : "none"} />
            </button>
          </div>
          <p className="text-xs text-ink-soft">{item.human_code} · {item.content_type} · v{latest.version_number} · {latest.status}</p>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {(latest.use_when || latest.develops) && (
            <div className="space-y-1">
              {latest.use_when && <p><span className="font-medium text-ink">Use when:</span> <span className="text-ink-soft">{latest.use_when}</span></p>}
              {latest.develops && <p><span className="font-medium text-ink">Develops:</span> <span className="text-ink-soft">{latest.develops}</span></p>}
            </div>
          )}
          {latest.use_case && <p><span className="font-medium text-ink">Use case:</span> <span className="text-ink-soft">{latest.use_case}</span></p>}
          {latest.outcome && <p><span className="font-medium text-ink">Outcome:</span> <span className="text-ink-soft">{latest.outcome}</span></p>}

          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-soft">
            {latest.duration_minutes && <span className="flex items-center gap-1"><Clock size={12} /> {latest.duration_minutes} min</span>}
            {latest.individual_or_team?.length > 0 && <span className="flex items-center gap-1"><Users size={12} /> {latest.individual_or_team.join(", ")}</span>}
            {latest.difficulty && <span className="bg-ink/5 rounded px-1.5 py-0.5">{latest.difficulty}</span>}
            {latest.audit_eligible && <span className="bg-terracotta/10 text-terracotta rounded px-1.5 py-0.5">Audit-eligible</span>}
          </div>

          {latest.steps?.length > 0 && (
            <div data-testid={`library-quick-preview-steps-${item.lib_id}`}>
              <p className="text-xs font-medium text-ink flex items-center gap-1 mb-1.5"><ListChecks size={12} /> Steps</p>
              <ol className="space-y-1.5 list-decimal list-inside text-ink-soft">
                {[...latest.steps].sort((a, b) => a.order_index - b.order_index).map((s, i) => (
                  <li key={i}><span className="font-medium text-ink">{s.title}</span>{s.body_text ? ` — ${s.body_text}` : ""}</li>
                ))}
              </ol>
            </div>
          )}

          {(assetsData?.items?.length || 0) > 0 && (
            <div data-testid={`library-quick-preview-assets-${item.lib_id}`}>
              <p className="text-xs font-medium text-ink flex items-center gap-1 mb-1.5"><Paperclip size={12} /> Assets</p>
              <div className="flex flex-wrap gap-1.5">
                {assetsData.items.map((a) => (
                  <span key={a.lba_id} className="text-xs bg-ink/5 rounded px-2 py-0.5" title={a.display_filename}>{ROLE_LABEL[a.role] || a.role}</span>
                ))}
              </div>
            </div>
          )}

          {(linksData?.items?.length || 0) > 0 && (
            <div data-testid={`library-quick-preview-links-${item.lib_id}`}>
              <p className="text-xs font-medium text-ink flex items-center gap-1 mb-1.5"><Link2 size={12} /> Linked frameworks/exercises</p>
              <div className="flex flex-wrap gap-1.5">
                {linksData.items.map((l) => (
                  <span key={l.lnk_id} className="text-xs bg-ink/5 rounded px-2 py-0.5">
                    {l.linked_item ? `${l.linked_item.human_code} ${l.linked_item.title}` : l.linked_lib_id}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div data-testid={`library-quick-preview-usage-${item.lib_id}`}>
            <p className="text-xs font-medium text-ink flex items-center gap-1 mb-1.5"><History size={12} /> Recent usage</p>
            {(usageData?.items?.length || 0) === 0 ? (
              <p className="text-xs text-ink-soft">Not assigned yet.</p>
            ) : (
              <ul className="text-xs text-ink-soft space-y-1">
                {usageData.items.map((u) => (
                  <li key={u.asg_id}>{u.title_snapshot} — {new Date(u.created_at).toLocaleDateString()} ({u.status})</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
